import { useReducer, useCallback, useRef, useEffect } from 'react';
import { logError, logEvent } from '@cortex/faro';
import { config } from '../config.js';
import type {
  ChatState,
  ChatAction,
  ChatMessage,
  ToolCall,
  SSEEvent,
  SessionEvent,
  ToolCallEvent,
  ToolResultEvent,
  ContentEvent,
  ErrorEvent,
  DashboardContext,
} from '../types/chat.js';

// 初期状態
/** @graph-connects none */
const initialState: ChatState = {
  sessionId: null,
  messages: [],
  isStreaming: false,
  error: null,
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business チャットUIのステートリデューサー。メッセージ追加・ストリーミング更新・ツールコール追跡・セッション管理・リセットのアクションをChatStateに反映
 * @graph-connects none
 */
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_SESSION':
      return { ...state, sessionId: action.sessionId };

    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };

    case 'UPDATE_LAST_MESSAGE': {
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        messages[messages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + action.content,
        };
      }
      return { ...state, messages };
    }

    case 'ADD_TOOL_CALL': {
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        messages[messages.length - 1] = {
          ...lastMessage,
          toolCalls: [...(lastMessage.toolCalls ?? []), action.toolCall],
        };
      }
      return { ...state, messages };
    }

    case 'UPDATE_TOOL_CALL': {
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.toolCalls) {
        const updatedToolCalls = lastMessage.toolCalls.map((tc) =>
          tc.id === action.id
            ? { ...tc, status: action.status, summary: action.summary, data: action.data }
            : tc,
        );
        messages[messages.length - 1] = {
          ...lastMessage,
          toolCalls: updatedToolCalls,
        };
      }
      return { ...state, messages };
    }

    case 'SET_STREAMING':
      return { ...state, isStreaming: action.isStreaming };

    case 'SET_ERROR':
      return { ...state, error: action.error, isStreaming: false };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business crypto.randomUUID()でメッセージ用のユニークIDを生成
 * @graph-connects none
 */
function generateId(): string {
  return crypto.randomUUID();
}

interface UseChatOptions {
  /** APIエンドポイント（例: /chat/kpi） */
  endpoint: string;
  /** 既存のセッションID（オプション） */
  initialSessionId?: string;
  /** 応答言語（省略時はja） */
  locale?: 'ja' | 'en' | 'vi';
  /** KPIダッシュボードのコンテキスト（KPIチャット用） */
  dashboardContext?: DashboardContext;
}

interface UseChatReturn {
  /** セッションID */
  sessionId: string | null;
  /** メッセージ一覧 */
  messages: ChatMessage[];
  /** ストリーミング中かどうか */
  isStreaming: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** メッセージ送信 */
  sendMessage: (message: string) => Promise<void>;
  /** セッションリセット */
  reset: () => void;
  /** エラークリア */
  clearError: () => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business SSEストリーミング対応の汎用チャットフック。メッセージ送受信・ツールコール追跡・セッション管理をuseReducerで提供
 * @graph-connects /chat [calls] Chat APIにSSEストリーミング接続（エンドポイントはオプションで指定）
 * @graph-connects config [calls] APIベースURL取得のためアプリケーション設定を参照
 */
export function useChat({
  endpoint,
  initialSessionId,
  locale,
  dashboardContext,
}: UseChatOptions): UseChatReturn {
  const [state, dispatch] = useReducer(chatReducer, {
    ...initialState,
    sessionId: initialSessionId ?? null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // コンポーネントアンマウント時にストリームを中断
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || state.isStreaming) return;

      // 前のリクエストを中断
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // ユーザーメッセージを追加
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: message,
        createdAt: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', message: userMessage });

      // アシスタントメッセージのプレースホルダーを追加
      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: '',
        createdAt: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', message: assistantMessage });

      dispatch({ type: 'SET_STREAMING', isStreaming: true });
      dispatch({ type: 'CLEAR_ERROR' });

      try {
        // sessionIdがnullの場合は送信しない（新規セッション作成）
        const requestBody: {
          message: string;
          sessionId?: string;
          locale?: string;
          dashboardContext?: DashboardContext;
        } = { message };
        if (state.sessionId) {
          requestBody.sessionId = state.sessionId;
        }
        if (locale) {
          requestBody.locale = locale;
        }
        if (dashboardContext) {
          requestBody.dashboardContext = dashboardContext;
        }

        logEvent('chat_send_started', {
          endpoint,
          sessionId: state.sessionId ?? 'new',
          locale: locale ?? 'ja',
          hasDashboardContext: String(dashboardContext !== undefined),
        });

        const response = await fetch(`${config.apiUrl}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const httpError = new Error(`HTTP ${String(response.status)}`);
          // 4xx はクライアント条件 (認証切れ・権限不足・バリデーション) でサービス障害ではないため
          // logEvent で trace 用に記録するだけにとどめ、5xx だけ logError (= Faro exception) で
          // 上げて Grafana の Service Error Log Detected アラート対象にする。api.ts の
          // request() と同じ 5xx-only 方針 (cortex-admin の request() も同様)。
          const logContext = {
            event: response.status >= 500 ? 'chat_http_error' : 'chat_http_client_error',
            endpoint,
            sessionId: state.sessionId ?? 'new',
            status: String(response.status),
          };
          if (response.status >= 500) {
            logError(httpError, logContext);
          } else {
            logEvent(logContext.event, logContext);
          }
          dispatch({
            type: 'SET_ERROR',
            error: httpError.message,
          });
          return;
        }

        if (!response.body) {
          const emptyBodyError = new Error('Response body is empty');
          logError(emptyBodyError, {
            event: 'chat_empty_response_body',
            endpoint,
            sessionId: state.sessionId ?? 'new',
          });
          dispatch({
            type: 'SET_ERROR',
            error: emptyBodyError.message,
          });
          return;
        }

        // SSEストリームを処理
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let done = false;
        const sseLogContext = {
          endpoint,
          sessionId: state.sessionId ?? 'new',
        };

        while (!done) {
          const result = await reader.read();
          done = result.done;

          if (done) continue;

          buffer += decoder.decode(result.value, { stream: true });

          // SSEイベントを解析
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          processSSELines(lines, dispatch, sseLogContext);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // キャンセルされた場合は何もしない
          return;
        }
        // fetch 自体の reject (`TypeError: Failed to fetch`) はブラウザ側の transient な状況
        // (ナビゲーション中の abort・オフライン・タブ切替・CORS) が大半でサービス障害ではない。
        // logEvent で記録だけ残し、Grafana の Service Error Log Detected アラート対象から外す。
        // それ以外の例外 (SSE パース throw 等の本物のバグ) は logError で残す。
        const error = err instanceof Error ? err : new Error(String(err));
        const isFetchNetworkReject = error.name === 'TypeError';
        const logContext = {
          event: isFetchNetworkReject ? 'chat_send_network_failed' : 'chat_send_failed',
          endpoint,
          sessionId: state.sessionId ?? 'new',
          errorName: error.name,
        };
        if (isFetchNetworkReject) {
          logEvent(logContext.event, logContext);
        } else {
          logError(error, logContext);
        }
        dispatch({
          type: 'SET_ERROR',
          error: error.message,
        });
      } finally {
        dispatch({ type: 'SET_STREAMING', isStreaming: false });
      }
    },
    [endpoint, state.sessionId, state.isStreaming, locale, dashboardContext],
  );

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    dispatch({ type: 'RESET' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return {
    sessionId: state.sessionId,
    messages: state.messages,
    isStreaming: state.isStreaming,
    error: state.error,
    sendMessage,
    reset,
    clearError,
  };
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business SSEストリームの行配列を解析し、各data行のJSONをパースしてイベントハンドラーにディスパッチ
 * @graph-connects none
 */
function processSSELines(
  lines: string[],
  dispatch: React.Dispatch<ChatAction>,
  logContext: { endpoint: string; sessionId: string },
) {
  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;

    const data = line.slice(6);
    if (data === '[DONE]') continue;

    try {
      const event = JSON.parse(data) as SSEEvent;
      handleEvent(event, dispatch, logContext);
    } catch (error) {
      // JSON 解析エラーは個別 SSE 行のみ無視。SyntaxError 以外は再 throw
      if (!(error instanceof SyntaxError)) {
        throw error;
      }
      logError(error, {
        event: 'sse_json_parse_failed',
        endpoint: logContext.endpoint,
        sessionId: logContext.sessionId,
      });
    }
  }
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business SSEイベント種別（session/tool_call/tool_result/content/done/error）に応じたReducerアクションをディスパッチ
 * @graph-connects none
 */
function handleEvent(
  event: SSEEvent,
  dispatch: React.Dispatch<ChatAction>,
  logContext: { endpoint: string; sessionId: string },
) {
  switch (event.type) {
    case 'session': {
      const sessionEvent = event as SessionEvent;
      logContext.sessionId = sessionEvent.sessionId;
      dispatch({ type: 'SET_SESSION', sessionId: sessionEvent.sessionId });
      break;
    }

    case 'tool_call': {
      const toolCallEvent = event as ToolCallEvent;
      const toolCall: ToolCall = {
        id: toolCallEvent.id,
        name: toolCallEvent.name,
        params: toolCallEvent.params,
        status: 'running',
      };
      dispatch({ type: 'ADD_TOOL_CALL', toolCall });
      break;
    }

    case 'tool_result': {
      const toolResultEvent = event as ToolResultEvent;
      dispatch({
        type: 'UPDATE_TOOL_CALL',
        id: toolResultEvent.id,
        status: toolResultEvent.success ? 'success' : 'error',
        summary: toolResultEvent.summary,
        data: toolResultEvent.data,
      });
      break;
    }

    case 'content': {
      const contentEvent = event as ContentEvent;
      dispatch({ type: 'UPDATE_LAST_MESSAGE', content: contentEvent.content });
      break;
    }

    case 'error': {
      const errorEvent = event as ErrorEvent;
      // SSE 経由でサーバから報告されたエラー。サマリで置換せずメッセージを全件残す。
      logError(new Error(errorEvent.message), {
        event: 'chat_sse_error_event',
        endpoint: logContext.endpoint,
        sessionId: logContext.sessionId,
      });
      dispatch({ type: 'SET_ERROR', error: errorEvent.message });
      break;
    }

    case 'done':
      // 完了イベントは特に処理なし
      break;
  }
}
